import Dropdown from "@/lib/Dropdown";
import List, { Column } from "@/lib/List";
import Menu from "@/lib/Menu";
import { useDeleteRfq } from "@/lib/queries/hooks";
import $api from "@/lib/queries/openapi";
import { RFQListItem } from "@/lib/queries/types";
import { getUrl } from "@/lib/useToPage";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { useParams } from "react-router";
import DropdownIcon from "~icons/local/dropdown";
import { datetimeFormatShort } from "../rfq/timeline/utils";
import RFQPartsTableCell from "./RFQPartsTableCell";
import RFQStatusTableCell from "./RFQStatusTableCell";

export default function RFQList({
  rfqs,
  isLoading,
  nonDefaultFilters,
}: {
  rfqs: RFQListItem[];
  isLoading: boolean;
  nonDefaultFilters: boolean;
}) {
  const deleteRfq = useDeleteRfq();

  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const { mutate: markExternalResponse } = $api.useMutation(
    "post",
    "/api/teams/{team_slug}/quote_revisions/{id}/responded_externally",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get", "/api/teams/{team_slug}/rfqs"],
        });
      },
    },
  );

  return (
    <List<RFQListItem>
      columns={[
        ...columns,
        { content: menu, skipLink: true, className: "w-16" },
      ]}
      list={rfqs}
      rowHref={(row: RFQListItem) => {
        if (row.processing) return "#";
        return getUrl("rfq", {
          teamSlug: teamSlug!,
          rfqId: row.id,
        });
      }}
      isLoading={isLoading}
      emptyState={
        <div className="text-center text-gray-500 py-8">
          No RFQs found{nonDefaultFilters ? "." : " yet."}
          {nonDefaultFilters && " Try changing the filters or search criteria."}
        </div>
      }
    />
  );

  function menu(row: RFQListItem) {
    return (
      <div>
        <Dropdown
          trigger={
            <DropdownIcon className="px-2 py-1 rounded-md hover:bg-gray-200 cursor-pointer size-8" />
          }
          placement="bottom-end"
        >
          <Menu
            itemGroups={[
              [
                ...(row.status !== "external_response"
                  ? [
                      {
                        label: "External Response",
                        click: () =>
                          markExternalResponse(
                            {
                              params: {
                                path: {
                                  team_slug: teamSlug!,
                                  id: row.latest_quote_revision_id || "",
                                },
                              },
                            },
                            {
                              onSuccess: () => {
                                queryClient.invalidateQueries({
                                  queryKey: [
                                    "get",
                                    "/api/teams/{team_slug}/rfqs",
                                  ],
                                });
                                toast.success(
                                  "RFQ status is updated to External Response",
                                );
                              },
                              onError: () => {
                                toast.error("Failed to update RFQ status");
                              },
                            },
                          ),
                      },
                    ]
                  : []),
                {
                  label: "Delete",
                  className: "text-red-700 hover:bg-red-50",
                  click: () =>
                    deleteRfq(row.id, {
                      onSuccess: () => toast.success("RFQ deleted"),
                      onError: (error) => toast.error(error.message),
                    }),
                },
              ],
            ]}
          />
        </Dropdown>
      </div>
    );
  }
}

const columns: Column<RFQListItem>[] = [
  // py-x are important here so every td has the same height and is a clickable link
  {
    label: "RFQ",
    content: ({ rfq_number }) => `#${rfq_number}`,
    className: "px-4 py-4",
  },
  {
    label: "Company",
    content: ({ contact }) => (
      <div className="font-bold capitalize">{contact.organization.name}</div>
    ),
    className: "px-4 py-4 text-nowrap",
  },
  {
    label: "Contact",
    content: ({ contact }) => contact.name,
    className: "px-4 py-4 text-nowrap",
  },
  {
    label: "Parts",
    content: (rfq) => <RFQPartsTableCell rfq={rfq} />,
    className: "px-4 py-2",
  },
  {
    label: "Status",
    content: (rfq) => <RFQStatusTableCell rfq={rfq} />,
    className: "px-4 py-4.5",
  },
  {
    label: "Date Received",
    content: ({ created_at }: RFQListItem) =>
      dayjs(created_at).format(datetimeFormatShort),
    className: "px-4  py-4 text-nowrap",
  },
];
